import React, { useState } from 'react'
import '@resources/css/tabs/genetics.css'
import IDonor from '@interfaces/IDonor'
import { FaCheck, FaFile, FaImage, FaInfoCircle, FaTimes } from 'react-icons/fa'
import IExamDocument from '@interfaces/IExamDocument'
import ITecleMutation from '@interfaces/ITecleMutation'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import Lightbox from 'yet-another-react-lightbox'
import Download from 'yet-another-react-lightbox/plugins/download'
import Share from 'yet-another-react-lightbox/plugins/share'
import Captions from 'yet-another-react-lightbox/plugins/captions'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import 'yet-another-react-lightbox/styles.css'
import 'yet-another-react-lightbox/plugins/captions.css'
import PdfLightbox from '@components/PdfLightbox/PdfLightbox'
import iconProseed from '@resources/imgs/icon-proseed.png'

const TCLE_VIDEO_ID = 'tValnvIalmk'
const GENETICS_COUNSELING_WHATSAPP_URL = 'https://api.whatsapp.com/send/?phone=5511983180033&text=Quero+agendar+consulta+com+geneticista&type=phone_number&app_absent=0'

type Props = {
  donor: IDonor
}

const Genetics: React.FC<Props> = ({ donor }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [currentImage, setCurrentImage] = useState<string>('')
  const [currentImageTitle, setCurrentImageTitle] = useState<string>('')
  const [pdfLightbox, setPdfLightbox] = useState<{ url: string; title?: string } | null>(null)
  const [showTecleVideo, setShowTecleVideo] = useState<boolean>(false)

  const handleImageClick = (file: string, title: string) => {
    setCurrentImage(file)
    setCurrentImageTitle(title)
    setIsOpen(true)
  }

  const cgtExomeTooltip = (
    <Tooltip id="genetics-cgt-exome-tooltip">
      O mapeamento genético do doador é só metade da informação necessária para análise de compatibilidade genética. Confira documento anexo para mais explicações do mapeamento genético!
    </Tooltip>
  )

  return (
    <section className="donator-about">
      <div className="genetics-intro-block">
        <p>
          O Pro-Seed, Seattle Sperm Bank &amp; European Sperm Bank realizam mapeamentos genéticos expandidos em seus doadores de sêmen. Esse exame permite que você avalie a compatibilidade genética com o doador escolhido, reduzindo o risco de transmissão de doenças recessivas ao bebê.
        </p>
        <p>
          O exame que realizamos no doador é só metade da informação, a outra metade vem do seu mapeamento (ou fonte de óvulos). Somente comparando os dois é possível identificar riscos compartilhados.
        </p>
        <p className="genetics-intro-list-title">Saiba que:</p>
        <ul className="genetics-intro-list">
          <li><FaCheck className="genetics-intro-check" /><span>É comum e esperado que o doador seja portador saudável de uma ou mais variantes genéticas recessivas – somos todos portadores!</span></li>
          <li><FaCheck className="genetics-intro-check" /><span>Variantes recessivas não afetam a saúde do doador, nem a segurança ou a qualidade da amostra ou do tratamento, e só representam risco ao bebê se o óvulo tiver a mesma variante.</span></li>
          <li><FaCheck className="genetics-intro-check" /><span>Estatisticamente, a probabilidade de incompatibilidade genética entre você e o doador é de apenas 5%.</span></li>
          <li><FaCheck className="genetics-intro-check" /><span>Painéis mais amplos (pelo menos 400 genes) oferecem mais transparência, segurança e tranquilidade para você e seu tratamento.</span></li>
        </ul>
        <button
          type="button"
          className="btn genetics-understand-more-btn genetics-understand-more-btn--pink"
          onClick={() => setShowTecleVideo(true)}
        >
          Entenda mais
        </button>
      </div>
      <div className="genetic">
        <h5>
          <span>Exames Genéticos do Doador</span>
        </h5>
        <div className="row">
          {donor.cgt_exome && (
            <div className="col-12 col-lg-6">
              <div>
                <span>Mapeamento Genético:</span> {donor.cgt_exome}
                <OverlayTrigger placement="top" overlay={cgtExomeTooltip}>
                  <span className="ml-1"><FaInfoCircle /></span>
                </OverlayTrigger>
              </div>
            </div>
          )}
          {donor.mapped_genes && (
            <div className="col-12 col-lg-3">
              <p><span className="text-capitalize">Genes Mapeados:</span> {donor.mapped_genes}</p>
            </div>
          )}
          <div className="col-12 col-lg-3">
            <p><span className="text-capitalize">Cariótipo:</span> {donor.karyotype || '46 XY, Normal'}</p>
          </div>
        </div>
      </div>
      {donor.tecle_mutations && donor.tecle_mutations.length > 0 && (
        <div className="genetic">
          <h5>
            <span>Variantes Genéticas Encontradas (TCLE)</span>
            <span
              className="ml-1 genetic-mutations-info-icon"
              role="button"
              tabIndex={0}
              title="Clique para assistir o vídeo explicativo sobre TCLE."
              onClick={e => {
                e.preventDefault()
                setShowTecleVideo(true)
              }}
            >
              <FaInfoCircle />
            </span>
          </h5>
          <div className="row">
            {donor.tecle_mutations.map((mutation: ITecleMutation, index: number) => (
              <div className="col-12" key={mutation.id}>
                <p className="genetic-mutation-item">{index + 1}. {mutation.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {donor.genetics_documents && donor.genetics_documents.length > 0 && (
        <div className="content exam-documents">
          <h5 className="mb-4">Exames</h5>
          <div className="">
            {donor.genetics_documents.map((doc: IExamDocument) => (
              <div className="exam-document-item text-center" key={doc.id}>
                <p className="mb-0"><span>{doc.name}</span></p>
                {doc.type === 'pdf' ? (
                  <OverlayTrigger placement="top" overlay={<Tooltip id={`tt-genetics-pdf-${doc.id}`}>Abrir exame em PDF</Tooltip>}>
                    <button
                      type="button"
                      className="btn btn-outline-secondary d-flex justify-content-center align-items-center"
                      onClick={() => setPdfLightbox({ url: doc.file, title: doc.name })}
                    >
                      <FaFile />
                    </button>
                  </OverlayTrigger>
                ) : doc.type === 'link' ? (
                  <OverlayTrigger placement="top" overlay={<Tooltip id={`tt-genetics-link-${doc.id}`}>Abrir link em nova aba</Tooltip>}>
                    <a href={doc.file} target="_blank" rel="noreferrer">
                      <button className="btn btn-outline-secondary d-flex justify-content-center align-items-center">
                        <FaFile />
                      </button>
                    </a>
                  </OverlayTrigger>
                ) : (
                  <OverlayTrigger placement="top" overlay={<Tooltip id={`tt-genetics-img-${doc.id}`}>Ver imagem do exame</Tooltip>}>
                    <button
                      className="btn btn-outline-secondary d-flex justify-content-center align-items-center"
                      onClick={() => handleImageClick(doc.file, doc.name)}
                    >
                      <FaImage />
                    </button>
                  </OverlayTrigger>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="genetics-counseling-block">
        <div className="genetics-counseling-header">
          <img src={iconProseed} alt="Diferencial Pro-Seed" className="genetics-counseling-icon" />
          <h5 className="mb-0">Aconselhamento Genético Patrocinado</h5>
        </div>
        <p>
          Para que você se sinta segura e tome uma decisão bem-informada, o Pro-Seed oferece uma consulta com geneticista especializada, que poderá explicar o(s) laudo(s) e esclarecer todas as suas dúvidas! Escolha seu(s) doador(es) antes de agendar essa conveniência.
          <a href={GENETICS_COUNSELING_WHATSAPP_URL} target="_blank" rel="noopener noreferrer"> Agendar consulta</a>
        </p>
      </div>
      {isOpen && (
        <Lightbox
          open={isOpen}
          close={() => setIsOpen(false)}
          slides={[{ src: currentImage, title: currentImageTitle }]}
          carousel={{ finite: true, }}
          plugins={[Download, Share, Captions, Zoom]}
          render={{
            buttonPrev: () => null,
            buttonNext: () => null,
          }}
        />
      )}
      {showTecleVideo && (
        <div
          className="video-lightbox-overlay"
          onClick={() => setShowTecleVideo(false)}
        >
          <div className="video-lightbox-content" onClick={e => e.stopPropagation()}>
            <button
              type="button"
              className="video-lightbox-close"
              onClick={() => setShowTecleVideo(false)}
              aria-label="Fechar"
            >
              <FaTimes size={20} />
            </button>
            <div className="video-lightbox-ratio">
              <iframe
                src={`https://www.youtube.com/embed/${TCLE_VIDEO_ID}?autoplay=1`}
                title="Vídeo explicativo sobre TCLE"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="video-lightbox-iframe"
              />
            </div>
          </div>
        </div>
      )}
      {pdfLightbox && (
        <PdfLightbox
          isOpen={!!pdfLightbox}
          fileUrl={pdfLightbox.url}
          title={pdfLightbox.title}
          onClose={() => setPdfLightbox(null)}
        />
      )}
    </section>
  )
}

export default Genetics
